import get from 'lodash.get';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import getConfig from 'next/config';
import i18next from 'i18next';
import { withCookies, Cookies } from 'react-cookie';
import classNames from 'classnames';

import {
  hideProviderSelectAction,
  loadScheduleAction,
  showProviderSelectAction,
} from 'redux/modules/tve';

import {
  configTempPass,
  TVE_CANONICAL_ROUTE,
  ACCESS_ENABLER_OBJ,
} from 'lib/tve';

import { ERROR_TYPES } from 'components/TVE/Slate';
import IntObserver from 'components/IntObserver';
import Player from 'components/TVE/Player';
import ProviderSelect from 'components/TVE/ProviderSelect';
import TempPass from 'components/TVE/TempPass';
import TVECountdown from 'components/TVE/Countdown';
import WithTVEAuthProvider from 'components/TVE/AuthProvider';

const {
  publicRuntimeConfig: {
    DISABLE_AUTH_TVE_MSNBC = false,
  } = {},
} = getConfig();

const block = 'playmaker-tease';

function TeaseComponent(props) {
  // Do not render without a valid id
  const { id } = props;
  if (!id) return null;

  const iframeRef = React.useRef(null);

  const [inView, setInView] = React.useState(false);
  const [checkTempPass, setCheckTempPass] = React.useState(false);

  const {
    authenticated,
    currentProgram,
    hasTempPass,
    hideProviderSelect,
    loadSchedule,
    providerSelectVisible,
    showProviderSelect,
    title,
    vertical,
    videoError,
  } = props;

  const hasAuth = authenticated || hasTempPass;

  const canonicalURL = `/${TVE_CANONICAL_ROUTE}?icid=tve-dynamicup-tease`;

  // Actions on mount
  React.useEffect(() => {
    // Load vertical schedule
    if (!currentProgram || !currentProgram.title) {
      loadSchedule(vertical);
    }
  }, [id]);

  // User not authenticated with mvpd
  React.useEffect(() => {
    if (authenticated === false && hasTempPass === false) {
      // render tempPass component
      setCheckTempPass(true);
    } else if (authenticated === true && providerSelectVisible === true) {
      // Close MVPD overlay
      hideProviderSelect();
    }
  }, [authenticated]);

  const getExpiration = () => {
    const { expirationName } = configTempPass;
    const { cookies } = props;
    return parseInt(cookies.get(expirationName), 10);
  };

  const handleAction = () => {
    if (hasAuth) {
      window.location = canonicalURL;
    } else {
      showProviderSelect();
    }
  };

  return (
    <>
      {providerSelectVisible && (
        <ProviderSelect
          iframeRef={iframeRef}
          redirectUrl={`${get(window, ['location', 'origin'], '')}${canonicalURL}`}
        />
      )}
      <IntObserver
        callback={([entry]) => {
          // Component in view
          const { isIntersecting } = entry;
          if (isIntersecting) {
            setInView(true);
          }
        }}
        threshold={0}
      >
        <section className={block}>
          {inView && !DISABLE_AUTH_TVE_MSNBC && (
            <>
              <WithTVEAuthProvider
                iframeRef={iframeRef}
                disableBackgroundLogin
              />
              {videoError !== ERROR_TYPES.BROWSER && checkTempPass && (
                <TempPass checkOnMount />
              )}
            </>
          )}
          <div className={`${block}__player`}>
            <div className={`${block}__player-animated`}>
              <a href={canonicalURL} className={`${block}__player-link`}>
                {currentProgram && currentProgram.thumb && (
                  <video autoPlay loop muted playsInline>
                    <source src={currentProgram.thumb} type="video/mp4" />
                  </video>
                )}
              </a>
            </div>
            {hasAuth && (
              <div className={`${block}__player-live`}>
                <a href={canonicalURL} className={`${block}__player-link`}>
                  <Player
                    autoplayMuted
                    bypassAuth={DISABLE_AUTH_TVE_MSNBC}
                    externalControlbar={1}
                  />
                </a>
              </div>
            )}
            <button
              aria-label="player action"
              className={`${block}__player-action`}
              onClick={handleAction}
              type="button"
            >
              <span
                className={classNames(
                  `${block}__player-icon icon`,
                  {
                    'icon-lock': !authenticated && !hasTempPass,
                    'icon-volume-level-0': hasAuth,
                  },
                )}
              />
            </button>
          </div>
          {hasTempPass && (
            <div className={`${block}__wrap-labels`}>
              <a href={canonicalURL} className={`${block}__live-tag`}>
                {i18next.t('LIVE')}
              </a>
              <div className={`${block}__label`}>
                {i18next.t('Daily preview expires in')}
                &nbsp;
                <span className={`${block}__countdown`}>
                  <TVECountdown
                    end={getExpiration()}
                    onEndCallback={() => {
                      if (
                        typeof window !== 'undefined'
                        && typeof window[ACCESS_ENABLER_OBJ] !== 'undefined'
                        && typeof window[ACCESS_ENABLER_OBJ].logout === 'function'
                      ) {
                        window[ACCESS_ENABLER_OBJ].logout();
                      }
                    }}
                  />
                </span>
              </div>
            </div>
          )}
          <div className={`${block}__wrap-tags`}>
            {!hasTempPass && (
              <a href={canonicalURL} className={`${block}__live-tag`}>
                {i18next.t('LIVE')}
              </a>
            )}
            <a href={canonicalURL} className={`${block}__logo-tag`}>
              <span className="icon icon-msnbc-tv" />
            </a>
          </div>
          {(title || (currentProgram && currentProgram.title)) && (
            <div className={`${block}__title`}>
              <a href={canonicalURL}>
                {title || currentProgram.title}
              </a>
            </div>
          )}
        </section>
      </IntObserver>
    </>
  );
}

TeaseComponent.propTypes = {
  authenticated: PropTypes.bool,
  id: PropTypes.string,
  cookies: PropTypes.instanceOf(Cookies).isRequired,
  currentProgram: PropTypes.shape({
    title: PropTypes.string,
    thumb: PropTypes.string,
  }),
  hasTempPass: PropTypes.bool,
  hideProviderSelect: PropTypes.func,
  loadSchedule: PropTypes.func,
  providerSelectVisible: PropTypes.bool,
  showProviderSelect: PropTypes.func,
  title: PropTypes.string,
  vertical: PropTypes.string,
  videoError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
};

TeaseComponent.defaultProps = {
  authenticated: null,
  id: null,
  currentProgram: {},
  hasTempPass: false,
  hideProviderSelect: Function.prototype,
  loadSchedule: Function.prototype,
  providerSelectVisible: false,
  showProviderSelect: Function.prototype,
  title: null,
  vertical: null,
  videoError: false,
};

const mapStateToProps = ({ shared, tve, video }) => ({
  authenticated: tve.authenticated,
  currentProgram: {
    title: get(tve, ['epgCurrent', 'program', 'title']),
    thumb: get(tve, ['epgCurrent', 'thumbMd']),
  },
  hasTempPass: tve.hasTempPass,
  providerSelectVisible: tve.providerSelectVisible,
  vertical: shared.vertical,
  videoError: video.error,
});

const mapActionsToProps = (dispatch) => ({
  hideProviderSelect: () => dispatch(hideProviderSelectAction()),
  loadSchedule: (vertical) => dispatch(loadScheduleAction(vertical)),
  showProviderSelect: () => dispatch(showProviderSelectAction()),
});

const PlaymakerTease = connect(mapStateToProps, mapActionsToProps)(
  withCookies(TeaseComponent),
);

export { PlaymakerTease };
